import { createStore } from "vuex";
import router from "@/router";
import Keycloak from "keycloak-js";
import axios from "axios";

const initOptions = {
    url: process.env.VUE_APP_KEYCLOAK_URL,
    realm: process.env.VUE_APP_KEYCLOAK_REALM,
    clientId: process.env.VUE_APP_KEYCLOAK_CLIENT_ID
};

const keycloak = new Keycloak(initOptions);

const store = createStore({
    state: {
        detectors: null,
        tenants: null,
        token: null,
        authInitialized: false,
        responseStatus: null,
        responseError: [],
        rwmFormResponseStatus: null,
        rwmFormResponseError: "",
        rwmFormResponseMessage: "",
        isAdmin: false,
        tenantId: null,
        webhookBaseUrl: 'https://automation.alarm.software/webhook',
    },
    mutations: {
        setDetectors(state, payload) {
            state.detectors = payload.detectors;
        },
        setToken(state, token) {
            state.token = token;
        },
        setAuthInitialized(state, value) {
            state.authInitialized = value;
        },
        setResponseStatus(state, status) {
            state.responseStatus = status;
        },
        setResponseError(state, responseError) {
            state.responseError = responseError;
        },
        setRwmFormResponseStatus(state, status) {
            state.rwmFormResponseStatus = status;
        },
        setRwmFormResponseError(state, responseError) {
            state.rwmFormResponseError = responseError;
        },
        setRwmFormResponseMessage(state, responseMessage) {
            state.rwmFormResponseMessage = responseMessage;
        },
        setIsAdmin(state, value) {
            state.isAdmin = value;
        },
        setTenants(state, tenants) {
            state.tenants = tenants;
        },
        setTenantId(state, tenantId) {
            state.tenantId = tenantId;
        }
    },
    actions: {
        resetResponseError({ commit }) {
            commit("setResponseError", []);
        },
        async sendRwmForm(context, payload) {
            try {
                const axiosInstance = axios.create();

                const response = await axiosInstance.post(
                    `${context.getters.webhookBaseUrl}/rwmfeedback`,
                    payload,
                    {
                        headers: {
                            Authorization: `Bearer ${keycloak?.token}`,
                        },
                    }
                );
                context.commit("setRwmFormResponseStatus", response.status);
                context.commit("setRwmFormResponseError", "");
                context.commit("setRwmFormResponseMessage", "Formular erfolgreich abgeschickt.");
                console.log(response);
                return true;
            } catch (error) {
                context.commit("setRwmFormResponseStatus", error.code);
                context.commit("setRwmFormResponseError", error.message);
                context.commit("setRwmFormResponseMessage", "");
                return false;
            }
        },
        async loadTenants(context) {
            try {
                const axiosInstance = axios.create();

                const response = await axiosInstance.post(
                    `${context.getters.webhookBaseUrl}/rwmtenants`,
                    {},
                    {
                        headers: {
                            Authorization: `Bearer ${keycloak?.token}`,
                        },
                    }
                );
                context.commit("setTenants", response.data);
                context.commit("setResponseStatus", response.status);
                //context.commit("setResponseError", "");
                console.log(response);
                return response.data;
            } catch (error) {
                context.commit("setResponseStatus", error.code);
                context.commit("setResponseError", [...context.getters.responseError, {code: error.code, message: error.message}]);
            }
        },
        async getDeviceInfo(context, payload) {
            try {
                const axiosInstance = axios.create();

                const response = await axiosInstance.post(
                    `${context.getters.webhookBaseUrl}/rwmdevicestatus`,
                    {
                        deviceid: payload,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${keycloak?.token}`,
                        },
                    }
                );
                return response.data?.length ? response.data[0] : null;
            } catch (error) {
                context.commit("setResponseStatus", error.code);
                context.commit("setResponseError", [...context.getters.responseError, {code: error.code, message: error.message}]);
            }
        },
        async loadDetectors(context) {
            try {
                const axiosInstance = axios.create();

                console.log(`TOKEN: ${keycloak?.token}`);
                console.log(keycloak.idTokenParsed);

                const response = await axiosInstance.post(
                    `${context.getters.webhookBaseUrl}/rwmstatus`,
                    {
                        tenantid: context.getters.tenantId,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${keycloak?.token}`,
                        },
                    }
                );
                context.commit("setDetectors", { detectors: response.data ?? [] });
                context.commit("setResponseStatus", response.status);
                //context.commit("setResponseError", "");
                return response.data;
            } catch (error) {
                context.commit("setResponseStatus", error.code);
                context.commit("setResponseError", [...context.getters.responseError, {code: error.code, message: error.message}]);
            }
        },
        async initAuth({ commit, dispatch, getters }) {

            if (getters.authInitialized) return;

            try {
                const authenticated = await keycloak.init({
                    onLoad: "check-sso",
                    checkLoginIframe: false
                });
                commit("setToken", authenticated ? keycloak.token : null);
                commit("setIsAdmin", authenticated ? keycloak?.idTokenParsed?.isadmin : false);
                if(getters.tenantId === null) {
                    commit("setTenantId", authenticated ? keycloak?.idTokenParsed?.tenantid : null);
                }
            } finally {
                commit("setAuthInitialized", true);
            }

            if (keycloak.authenticated) {

                await dispatch("loadDetectors");

                if(getters.isAdmin) {
                    await dispatch("loadTenants");
                }

                console.log(`Is Admin: ${getters.isAdmin}`);

                setInterval(() => {
                    keycloak.updateToken(70).catch(() => {
                        commit("setToken", null);
                    });
                }, 6000);
            }
        },
        async login() {
            await keycloak.login(
                {
                    redirectUri: window.location.origin,
                }
            );
        },
        async logout() {
            const resolved = router.resolve({ name: "Logout" });
            const absoluteLogoutUrl = new URL(resolved.href, window.location.origin).toString();
            console.log(absoluteLogoutUrl);
            await keycloak.logout(
                {
                    redirectUri: absoluteLogoutUrl,
                }
            );
        }

    },
    getters: {
        isAuthenticated: (state) => !!state.token,
        isAdmin: (state) => !!state.isAdmin,
        detector: state => (id) => state.detectors?.find((detector) => detector.identifier === id),
        detectors: state => state.detectors,
        tenants: state => state.tenants,
        detectorsSortedByIdAsc: state => state.detectors ? state.detectors?.sort((a, b) =>
            String(a.identifier ?? "").localeCompare(String(b.identifier ?? ""))
        ) : [],
        detectorsSortedByIdDesc: state => state.detectors ? state.detectors?.sort((a, b) =>
            String(b.identifier ?? "").localeCompare(String(a.identifier ?? ""))
        ) : [],
        detectorsSortedByLastUpdateAsc: state => state.detectors ? state.detectors?.sort((a, b) =>
            String(a.LastUpdate ?? "").localeCompare(String(b.LastUpdate ?? ""))
        ) : [],
        detectorsSortedByLastUpdateDesc: state => state.detectors ? state.detectors?.sort((a, b) =>
            String(b.LastUpdate ?? "").localeCompare(String(a.LastUpdate ?? ""))
        ) : [],
        detectorsSortedByLastContactAsc: state => state.detectors ? state.detectors?.sort((a, b) =>
            String(a.LastContact ?? "").localeCompare(String(b.LastContact ?? ""))
        ) : [],
        detectorsSortedByLastContactDesc: state => state.detectors ? state.detectors?.sort((a, b) =>
            String(b.LastContact ?? "").localeCompare(String(a.LastContact ?? ""))
        ) : [],
        detectorsSortedByLocalityAsc: state => state.detectors ? state.detectors?.sort((a, b) =>
            String(a.Locality ?? "").localeCompare(String(b.Locality ?? ""))
        ) : [],
        detectorsSortedByLocalityDesc: state => state.detectors ? state.detectors?.sort((a, b) =>
            String(b.Locality ?? "").localeCompare(String(a.Locality ?? ""))
        ) : [],
        detectorsSortedByCurrentStateAsc: state => state.detectors ? state.detectors?.sort((a, b) =>
            String(a.currentstate ?? "").localeCompare(String(b.currentstate ?? ""))
        ) : [],
        detectorsSortedByCurrentStateDesc: state => state.detectors ? state.detectors?.sort((a, b) =>
            String(b.currentstate ?? "").localeCompare(String(a.currentstate ?? ""))
        ) : [],
        tenantsSortedByNameAsc: state => state.tenants ? state.tenants?.sort((a, b) =>
            String(a.Matchcode ?? "").localeCompare(String(b.Matchcode ?? ""))
        ) : [],
        tenantsSortedByNameDesc: state => state.tenants ? state.tenants?.sort((a, b) =>
            String(b.Matchcode ?? "").localeCompare(String(a.Matchcode ?? ""))
        ) : [],
        responseError: state => state.responseError,
        responseStatus: state => state.responseStatus,
        rwmFormResponseError: state => state.rwmFormResponseError,
        rwmFormResponseMessage: state => state.rwmFormResponseMessage,
        rwmFormResponseStatus: state => state.rwmFormResponseStatus,
        tenantId: state => state.tenantId,
        token: state => state.token,
        webhookBaseUrl: state => state.webhookBaseUrl,
        authInitialized: state => state.authInitialized,
    },
});

export default store;